import React, { useState, useEffect } from 'react';
import { Venue } from '../../types/venue';
import { XMarkIcon } from '@heroicons/react/24/outline';
import PitchGraphic from './PitchGraphic';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

interface VenueModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  venue?: Venue | null;
}

const VenueModal: React.FC<VenueModalProps> = ({ isOpen, onClose, onSave, venue }) => {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    description: '',
    capacity: 0,
    sections: 1,
  });
  // A részek nevei: mindig 4 hely, a mentésnél csak a részek számáig küldjük
  const [sectionNames, setSectionNames] = useState<string[]>(['', '', '', '']);

  useEffect(() => {
    if (venue) {
      setFormData({
        name: venue.name,
        address: venue.address || '',
        description: venue.description || '',
        capacity: venue.capacity,
        sections: venue.sections,
      });
      setSectionNames([0, 1, 2, 3].map((i) => venue.sectionNames?.[i] ?? ''));
    } else {
      setSectionNames(['', '', '', '']);
      setFormData({
        name: '',
        address: '',
        description: '',
        capacity: 0,
        sections: 1,
      });
    }
  }, [venue, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...formData, sectionNames: sectionNames.slice(0, formData.sections).map((n) => n.trim()) });
  };

  useEscapeToClose(isOpen, onClose);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true" aria-label={venue ? 'Pálya szerkesztése' : 'Új pálya hozzáadása'}>
      <div className="flex min-h-full items-stretch justify-center md:items-center md:p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <div className="modal-panel-mobile relative flex w-full flex-col transform bg-white shadow-xl transition-all md:max-w-md md:rounded-lg md:overflow-hidden">
          <div className="flex shrink-0 items-center justify-between border-b px-4 py-3 md:px-6 md:py-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {venue ? 'Pálya szerkesztése' : 'Új pálya hozzáadása'}
            </h3>
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
            <div className="flex-1 overflow-y-auto bg-white px-4 pt-4 pb-safe-4 md:px-6">
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    Név *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="address" className="block text-sm font-medium text-gray-700">
                    Cím
                  </label>
                  <input
                    type="text"
                    id="address"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                    Leírás
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="capacity" className="block text-sm font-medium text-gray-700">
                    Kapacitás *
                  </label>
                  <input
                    type="number"
                    id="capacity"
                    required
                    min="0"
                    value={formData.capacity}
                    onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) || 0 })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                  />
                </div>

                <div>
                  <label htmlFor="sections" className="block text-sm font-medium text-gray-700">
                    Részek száma (1-4) *
                  </label>
                  <select
                    id="sections"
                    required
                    value={formData.sections}
                    onChange={(e) => setFormData({ ...formData, sections: parseInt(e.target.value) })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-primary-500"
                  >
                    <option value={1}>1 rész (teljes pálya)</option>
                    <option value={2}>2 rész</option>
                    <option value={3}>3 rész</option>
                    <option value={4}>4 rész</option>
                  </select>
                </div>

                {/* A részek elnevezése: a foglalásnál és a foglaltságnál ez a név látszik */}
                {formData.sections > 1 && (
                  <div>
                    <span className="block text-sm font-medium text-gray-700">A részek neve (nem kötelező)</span>
                    <p className="mt-0.5 text-xs text-gray-500">
                      Pl. „Kapu felőli”, „Öltöző felőli”. Ha üresen hagyod, a rész a sorszámával szerepel.
                    </p>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {Array.from({ length: formData.sections }, (_, i) => (
                        <input
                          key={i}
                          type="text"
                          maxLength={40}
                          value={sectionNames[i]}
                          onChange={(e) =>
                            setSectionNames((prev) => prev.map((n, j) => (j === i ? e.target.value : n)))
                          }
                          aria-label={`${i + 1}. rész neve`}
                          placeholder={`${i + 1}. rész`}
                          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-base focus:border-primary-500 focus:outline-none focus:ring-primary-500 sm:text-sm"
                        />
                      ))}
                    </div>
                    <div className="mt-3 max-w-xs">
                      <PitchGraphic
                        sectionCount={formData.sections}
                        highlighted={null}
                        label="Így jelenik meg"
                        sectionNames={sectionNames.slice(0, formData.sections)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="shrink-0 bg-gray-50 pb-safe">
              <div className="px-4 py-3 flex flex-col gap-2 md:flex-row-reverse md:gap-3 md:px-6">
                <button
                  type="submit"
                  className="w-full md:w-auto inline-flex justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  {venue ? 'Mentés' : 'Létrehozás'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full md:w-auto inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  Mégse
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default VenueModal;
